import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { rewardTypeFilter } from "@/utils/filter/rewardTypeFilter";
import { getWayFilter } from "@/pages/coupon/constant/coupon-filter";
import { dateFormatter } from "project-config/utils/date-formatter";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { OperationButton } from "@/components/operation-button";
import routerUtil from "@/utils/routerUtil";
import { PaymentTypeCode } from "project-config/constants";
import request from "project-config/services/request/request";
import {
  RechargeInvoiceStatus,
  RechargeStatus,
} from "@/pages/payment/constants";
const t = getTranslateNsT("center");

const DESKTOP_RECHARGE_ACTION_AVAILABLE_STATUSES = [
  RechargeStatus.Paid,
  RechargeStatus.PendingReceiptConfirmation,
  RechargeStatus.Received,
];

export let isInvoice = [
  "",
  t("pay_not_invoice"),
  t("pay_can_invoice"),
  t("pay_is_invoice"),
];

export function useDesktopColumns({
  isSubAccount,
  isDomestic,
}: {
  isSubAccount: boolean;
  isDomestic: boolean;
}) {
  const { t } = useTranslation("center");

  const columns: ColumnsType<any> = [
    { dataIndex: "payRecordId", title: t("record_header_id") },
    { dataIndex: "couponNo", title: t("record_header_coupon_id") },
    {
      dataIndex: "type",
      title: t("record_header_coupon_type"),
      render: (row) => rewardTypeFilter(row),
    },
    {
      dataIndex: "amount",
      title: t("coupon_header_amount"),
      render: (row) => Number(row).toFixed(3),
    },
    {
      dataIndex: "couponUpdate",
      title: t("record_header_coupon_balance"),
      render: (row) => Number(row).toFixed(3),
    },
    {
      dataIndex: "payMethod",
      title: t("record_header_claim_method"),
      render: (row) => getTranslateNsT("coupon")(getWayFilter(row)),
    },
    {
      dataIndex: "inOutCome",
      title: t("record_header_type"),
      render: (row: number) =>
        ({ 1: t("income_type"), 2: t("outcome_type") }[row]),
    },
    {
      dataIndex: "expireDate",
      title: t("record_header_expire_date"),
      render: (row) => dateFormatter(row),
    },
    {
      dataIndex: "createDate",
      title: t("coupon_header_usetime"),
      render: (row) => dateFormatter(row),
    },
    {
      dataIndex: "relatedNo",
      title: t("coupon_header_source"),
      render: (row, r) => (row ? `${r.destName}:${row}` : ""),
    },
    {
      dataIndex: "isInvoice",
      title: t("record_header_invoice"),
      render: (_, row) => {
        return row.isInvoice === 2 ? (
          <>
            <span style={{ marginRight: "10px" }}>{t("pay_invoicing")}</span>
          </>
        ) : (
          isInvoice[row.isInvoice]
        );
      },
    },
    {
      dataIndex: "handle",
      title: t("record_header_operate"),
      render: (_, row) => {
        let state;
        if (
          row.payRecordId &&
          DESKTOP_RECHARGE_ACTION_AVAILABLE_STATUSES.includes(
            row.status as RechargeStatus
          )
        ) {
          state = "pay";
        }

        const btnStateObj = {
          continuePay: {
            click: async () => {
              const payMethod = row.payMethodId;
              let blockPopup = null;
              if (
                payMethod === PaymentTypeCode.Alipay ||
                payMethod === PaymentTypeCode.Paypal ||
                payMethod === 30
              ) {
                blockPopup = window.open("about:blank", "_blank") as Window;
                if (payMethod === PaymentTypeCode.Paypal) {
                  blockPopup.document.body.innerHTML = `<p>Please wait while you are being redirected...</p>`;
                }
              }
              let ret = await request.post(
                "/api/rendering/pay/recharge/paySecond",
                { outTradeNo: row.payRecordId }
              );
              if (ret.code !== 200) {
                return;
              }
              if (payMethod === PaymentTypeCode.Alipay || payMethod === 30) {
                blockPopup!.document.body.innerHTML = ret.data;
                blockPopup!.document.getElementsByTagName("form")[0].submit();
              }
              if (payMethod === PaymentTypeCode.Wechat || payMethod === 31) {
                // let QrcodeImgSrc = "data:image/png;base64," + ret.data;
                // todo Modal show Qrcode
              }
              if (row.payMethodId === 3) {
                blockPopup!.location = ret.data;
              }
            },
            btn: "record_header_operate_continue_pay",
          },
          pay: {
            click: () => {
              function createFormToDownload(
                url: string,
                params: Record<string, any>
              ) {
                let b = document.getElementsByTagName("body"),
                  i = "download" + Date.now(),
                  s = document.createElement("form");
                s.setAttribute("id", i);
                s.setAttribute("method", "post");
                s.setAttribute("action", url);
                s.setAttribute("target", "_blank");
                for (let k in params) {
                  let input = document.createElement("input");
                  input.setAttribute("name", k);
                  input.setAttribute("value", params[k]);
                  s.appendChild(input);
                }

                if (b && b[0]) {
                  b[0].appendChild(s);
                }
                s.submit();
                setTimeout(() => {
                  s.remove();
                }, 1600);
              }

              createFormToDownload(
                "/api/rendering/user/export/exportRechargePdf",
                { rechargeId: row.payRecordId }
              );
            },
            btn: "record_header_operate_download_order",
          },
        }[state ?? ""];

        if (btnStateObj) {
          return (
            <>
              <OperationButton
                onClick={btnStateObj.click}
                text={t(btnStateObj.btn)}
              />
              {isDomestic &&
                !isSubAccount &&
                row.isInvoice === RechargeInvoiceStatus.CanInvoice &&
                DESKTOP_RECHARGE_ACTION_AVAILABLE_STATUSES.includes(
                  row.status as RechargeStatus
                ) && (
                  <OperationButton
                    onClick={() => routerUtil.navigate?.("/invoice")}
                    text={t("pay_can_invoice")}
                  />
                )}
            </>
          );
        }
      },
    },
  ];

  return { columns };
}
